import type { BillEntryData } from '@strata-monorepo/shared-ui/components/BillEntry/BillEntry'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/**
 * Resolves month string (e.g. "January") to 0-based month index.
 * Returns -1 if not a valid month name.
 */
function monthNameToIndex(month: string): number {
  const normalized = month.trim()
  const index = MONTH_NAMES.findIndex((name) => name.toLowerCase() === normalized.toLowerCase())
  return index
}

/**
 * Returns whether the bill entry's month/year matches the calendar previous month.
 * Uses current date to compute "previous month". Handles January (previous year).
 */
export function isBillFromPreviousMonth(entry: { month: string; year: number | null }): boolean {
  if (!entry.month?.trim() || entry.year == null) return false
  const entryMonthIndex = monthNameToIndex(entry.month)
  if (entryMonthIndex < 0) return false

  const now = new Date()
  let prevMonth = now.getMonth() - 1
  let prevYear = now.getFullYear()
  if (prevMonth < 0) {
    prevMonth = 11
    prevYear -= 1
  }

  return entryMonthIndex === prevMonth && entry.year === prevYear
}

/**
 * Returns the previous calendar month name and year (for synthetic entries).
 */
function getPreviousMonthAndYear(): { month: string; year: number } {
  const now = new Date()
  let prevMonthIndex = now.getMonth() - 1
  let prevYear = now.getFullYear()
  if (prevMonthIndex < 0) {
    prevMonthIndex = 11
    prevYear -= 1
  }
  const month = MONTH_NAMES[prevMonthIndex] ?? 'January'
  return { month, year: prevYear }
}

/**
 * Creates a minimal BillEntryData for the consumption API when using slider value only.
 * Uses previous calendar month for date fields.
 */
export function createSyntheticBillEntry(usageKwh: number): BillEntryData {
  const { month, year } = getPreviousMonthAndYear()
  return {
    usage: usageKwh,
    month,
    day: 1,
    year,
    address: '',
    billing: null,
    planName: '',
    fileName: '',
    source: 'manual',
    isSubmitted: true,
    isEditing: false,
  }
}
